import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import { ShieldCheckIcon, BuildingOffice2Icon, ChartBarIcon, ClipboardDocumentListIcon, ArrowRightOnRectangleIcon } from '@heroicons/react/24/outline';
import navStyles from '../App.module.css';
import SuperAdminLogin from './SuperAdminLogin';
import SystemOverview from './SystemOverview';
import TenantList from './TenantList';
import TenantProvision from './TenantProvision';
import TenantDetail from './TenantDetail';
import SuperAdminAuditLog from './SuperAdminAuditLog';

const NAV_ITEMS = [
  { label: 'Schools', path: '/superadmin/schools', icon: BuildingOffice2Icon },
  { label: 'Overview', path: '/superadmin/overview', icon: ChartBarIcon },
  { label: 'Audit Log', path: '/superadmin/audit-log', icon: ClipboardDocumentListIcon },
];

function SuperAdminShell({ token, user, onLogout }) {
  const location = useLocation();

  return (
    <div className="App">
      <nav className={navStyles.navbar} style={{ background: '#0F172A' }}>
        <Link to="/superadmin/schools" className={navStyles.brand}>
          <div className={navStyles.brandIcon} style={{ background: '#4C1D95' }}>
            <ShieldCheckIcon style={{ width: 20, height: 20 }} />
          </div>
          <div>
            <div className={navStyles.brandName} style={{ color: 'white' }}>Platform Admin</div>
            <span className={navStyles.brandSub} style={{ color: '#94A3B8' }}>Super Admin Portal</span>
          </div>
        </Link>

        <div className={navStyles.navDesktop}>
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            const active = location.pathname.startsWith(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`${navStyles.navTrigger} ${active ? navStyles.navTriggerActive : ''}`}
                style={{ color: 'white' }}
              >
                <Icon style={{ width: 16, height: 16 }} />
                {item.label}
              </Link>
            );
          })}
        </div>

        <div className={navStyles.navRight}>
          <div className={navStyles.userInfo} style={{ color: 'white' }}>
            <span>{user?.full_name || user?.username}</span>
          </div>
          <button className={navStyles.logoutBtn} onClick={onLogout} style={{ color: 'white' }}>
            <ArrowRightOnRectangleIcon style={{ width: 16, height: 16 }} />
            <span className="no-print">Logout</span>
          </button>
        </div>
      </nav>

      <div className="container">
        <Routes>
          <Route path="/" element={<Navigate to="/superadmin/schools" replace />} />
          <Route path="schools" element={<TenantList token={token} />} />
          <Route path="schools/new" element={<TenantProvision token={token} />} />
          <Route path="schools/:id" element={<TenantDetail token={token} />} />
          <Route path="overview" element={<SystemOverview token={token} />} />
          <Route path="audit-log" element={<SuperAdminAuditLog token={token} />} />
        </Routes>
      </div>
    </div>
  );
}

function SuperAdminApp() {
  const [token, setToken] = useState(localStorage.getItem('superadmin_token'));
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(Boolean(token));

  useEffect(() => {
    if (!token) { setChecking(false); return; }
    (async () => {
      try {
        const response = await fetch('/api/v1/superadmin/schools', { headers: { Authorization: `Bearer ${token}` } });
        if (response.ok) {
          // Token is valid for the superadmin API; we don't have a dedicated
          // /superadmin/auth/me, so pull the identity out of the login
          // response we cached at login time instead.
          const cached = localStorage.getItem('superadmin_user');
          setUser(cached ? JSON.parse(cached) : null);
        } else {
          handleLogout();
        }
      } catch {
        // Network hiccup shouldn't force a logout; let the user retry.
      } finally {
        setChecking(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleLogin = (newToken, userData) => {
    localStorage.setItem('superadmin_token', newToken);
    localStorage.setItem('superadmin_user', JSON.stringify(userData));
    setToken(newToken);
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('superadmin_token');
    localStorage.removeItem('superadmin_user');
    setToken(null);
    setUser(null);
  };

  if (checking) return <div className="loading">Loading...</div>;
  if (!token) return <SuperAdminLogin onLogin={handleLogin} />;

  return <SuperAdminShell token={token} user={user} onLogout={handleLogout} />;
}

export default SuperAdminApp;
